import React, { useState } from 'react';
import { EmailBlock, HeaderBlockContent } from '../../types';
import { Plus, X } from 'lucide-react';
import { ReusableHeaderManager } from '../ReusableHeaderManager';

interface HeaderPropertiesProps {
  block: EmailBlock;
  onUpdate: (updates: Partial<EmailBlock>) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
  isDarkMode: boolean;
}

export const HeaderProperties: React.FC<HeaderPropertiesProps> = ({
  block,
  onUpdate,
  isDarkMode
}) => {
  const content = block.content as HeaderBlockContent;
  const [newNavText, setNewNavText] = useState('');
  const [newNavLink, setNewNavLink] = useState('');

  const handleAddNavigation = () => {
    if (!newNavText.trim() || !newNavLink.trim()) return;
    const navigation = [...(content.navigation || []), {
      text: newNavText,
      link: newNavLink
    }];
    handleContentUpdate({ navigation });
    setNewNavText('');
    setNewNavLink('');
  };

  const handleRemoveNavigation = (index: number) => {
    const navigation = content.navigation?.filter((_, i) => i !== index) || [];
    handleContentUpdate({ navigation });
  };

  const handleContentUpdate = (updates: Partial<HeaderBlockContent>) => {
    onUpdate({
      content: {
        ...block.content,
        ...updates
      }
    });
  };

  const handleSelectReusableHeader = (headerBlock: EmailBlock) => {
    onUpdate({
      content: {
        ...headerBlock.content,
        isReusable: true,
        reusableHeaderId: headerBlock.id
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* Reusable Headers Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <ReusableHeaderManager
          currentHeaderBlock={block}
          onSelectHeader={handleSelectReusableHeader}
          isDarkMode={isDarkMode}
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Background Color
        </label>
        <input
          type="color"
          value={content.backgroundColor || '#ffffff'}
          onChange={(e) => handleContentUpdate({ backgroundColor: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Padding
        </label>
        <input
          type="text"
          value={content.padding || '20px'}
          onChange={(e) => handleContentUpdate({ padding: e.target.value })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
          placeholder="20px"
        />
      </div>

      {/* Logo Section */}
      <div className="space-y-2">
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Logo
        </label>
        <div className="space-y-2">
          <div>
            <label className={`
              block text-xs mb-1
              ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
            `}>
              Logo URL
            </label>
            <input
              type="url"
              value={content.logo?.src || ''}
              onChange={(e) => handleContentUpdate({ 
                logo: { ...content.logo, src: e.target.value }
              })}
              className={`
                w-full px-3 py-2 border rounded-md text-sm
                ${isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
              placeholder="https://example.com/logo.png"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`
                block text-xs mb-1
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Alt Text
              </label>
              <input
                type="text"
                value={content.logo?.alt || ''}
                onChange={(e) => handleContentUpdate({ 
                  logo: { ...content.logo, alt: e.target.value }
                })}
                className={`
                  w-full px-3 py-2 border rounded-md text-sm
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                placeholder="Logo"
              />
            </div>
            <div>
              <label className={`
                block text-xs mb-1
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Width
              </label>
              <input
                type="text"
                value={content.logo?.width || '150px'}
                onChange={(e) => handleContentUpdate({ 
                  logo: { ...content.logo, width: e.target.value }
                })}
                className={`
                  w-full px-3 py-2 border rounded-md text-sm
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                placeholder="150px"
              />
            </div>
          </div>
          <div>
            <label className={`
              block text-xs mb-1
              ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
            `}>
              Logo Link (optional)
            </label>
            <input
              type="url"
              value={content.logo?.link || ''}
              onChange={(e) => handleContentUpdate({ 
                logo: { ...content.logo, link: e.target.value }
              })}
              className={`
                w-full px-3 py-2 border rounded-md text-sm
                ${isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
              placeholder="https://example.com"
            />
          </div>
        </div>
      </div>

      {/* Navigation Section */}
      <div className="space-y-2">
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Navigation Links
        </label>
        
        {content.navigation && content.navigation.length > 0 && (
          <div className="space-y-2 mb-2">
            {content.navigation.map((nav, index) => (
              <div key={index} className={`
                flex items-center gap-2 p-2 rounded
                ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}
              `}>
                <span className={`
                  text-sm flex-1
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  {nav.text} → {nav.link}
                </span>
                <button
                  onClick={() => handleRemoveNavigation(index)}
                  className={`
                    p-1 rounded hover:bg-opacity-80
                    ${isDarkMode ? 'text-error-400 hover:bg-gray-600' : 'text-error-600 hover:bg-gray-200'}
                  `}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={newNavText}
            onChange={(e) => setNewNavText(e.target.value)}
            className={`
              flex-1 px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="Link text"
          />
          <input
            type="url"
            value={newNavLink}
            onChange={(e) => setNewNavLink(e.target.value)}
            className={`
              flex-1 px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="URL"
          />
          <button
            onClick={handleAddNavigation}
            className={`
              px-3 py-2 rounded-md
              ${isDarkMode 
                ? 'bg-accent-600 hover:bg-accent-700 text-white' 
                : 'bg-accent-500 hover:bg-accent-600 text-white'
              }
            `}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
